import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TypeAnimation } from 'react-type-animation'

const RailIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4V20" stroke="#4285F4" strokeWidth="3" strokeLinecap="round" />
    <path d="M20 4V20" stroke="#EA4335" strokeWidth="3" strokeLinecap="round" />
    <path d="M4 8H20" stroke="#FBBC05" strokeWidth="2" strokeLinecap="round" />
    <path d="M4 16H20" stroke="#34A853" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const RoadIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2L4 22" stroke="#4285F4" strokeWidth="3" strokeLinecap="round" />
    <path d="M18 2L20 22" stroke="#EA4335" strokeWidth="3" strokeLinecap="round" />
    <path d="M11 4V7" stroke="#FBBC05" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 11V14" stroke="#34A853" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 18V21" stroke="#FBBC05" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const AirIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12V13C4 14.1046 4.89543 15 6 15H17.5L21 12H4Z" fill="#4285F4" />
    <path d="M14 15L11 20H13.5L16.5 15H14Z" fill="#EA4335" />
    <path d="M15 12L12 5H14.5L17.5 12H15Z" fill="#34A853" />
    <path d="M4 11H7L5 12H4V11Z" fill="#FBBC05" />
    <path d="M21 12L18 10L19 12L18 14L21 12Z" fill="#FBBC05" />
  </svg>
)

const MODES = [
  { id: 'rail', component: RailIcon },
  { id: 'road', component: RoadIcon },
  { id: 'air', component: AirIcon },
  { id: 'typing', component: null },
]

interface XLineLogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showSlogan?: boolean
}

export function XLineLogo({ className = '', size = 'md', showSlogan = false }: XLineLogoProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const intervals = [3000, 3000, 3000, 6000] // More time for typing
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % MODES.length)
    }, intervals[currentIndex])
    return () => clearTimeout(timer)
  }, [currentIndex])

  const currentMode = MODES[currentIndex]

  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-5xl',
    xl: 'text-7xl',
  }

  const iconSizes = {
    sm: 20,
    md: 28,
    lg: 44,
    xl: 60,
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className={`flex items-center gap-2 font-black tracking-tighter ${sizeClasses[size]}`}>
        <span className="text-foreground">X</span>

        <div className="relative inline-flex items-center justify-center min-h-[1em] min-w-[1em]">
          <AnimatePresence mode="wait">
            {currentMode.id === 'typing' ? (
              <motion.span
                key="typing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-foreground text-[0.75em] whitespace-nowrap"
              >
                <TypeAnimation
                  sequence={[
                    'Line', 1500, '',
                    'By Djotta', 1500, '',
                  ]}
                  wrapper="span"
                  cursor={false}
                  repeat={0}
                />
              </motion.span>
            ) : (
              <motion.div
                key={currentMode.id}
                initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: 180 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="inline-flex items-center justify-center text-foreground"
              >
                {currentMode.component && <currentMode.component size={iconSizes[size]} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {currentMode.id !== 'typing' && (
          <span className="text-foreground opacity-0 pointer-events-none">Line</span>
        )}
      </div>

      {showSlogan && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mt-1 ml-1"
        >
          All routes, one intelligence
        </motion.p>
      )}
    </div>
  )
}
